import { Button, Card, Col, Flex, Row, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Table } from 'antd/lib'
import cronstrue from 'cronstrue'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePatientTreatmentServiceGetPatientConsultationEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpoint,
  usePatientTreatmentServiceGetPatientTreatmentPlanEndpoint,
  usePersonsServiceGetPersonEndpoint
} from 'src/api/queries'
import {
  GetTreatmentPlanPrescribedTreatmentResponse,
  TreatmentPlanStatus
} from 'src/api/requests'
import { PageBreadcrumbs, Text } from 'src/components'
import { TreatmentStatusTag } from 'src/features/persons/components'
import { dayMonthYearBackSlash, formatDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { CollapseDetails, ReactionsModal } from '../components'
import { useStyles } from '../styles'

import 'cronstrue/locales/ro'
import 'cronstrue/locales/ru'

dayjs.extend(duration)
dayjs.extend(relativeTime)

export const TreatmentDetails = () => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const onModalCancel = () => {
    setIsModalOpen(false)
  }

  const onClick = () => {
    setIsModalOpen(true)
  }

  const { styles } = useStyles()

  const { data: person } = usePersonsServiceGetPersonEndpoint()

  const { data, isLoading } =
    usePatientTreatmentServiceGetPatientTreatmentPlanEndpoint({
      id: String(id)
    })

  const { data: latestConsultation, isLoading: isLoadingConsultations } =
    usePatientTreatmentServiceGetPatientConsultationEndpoint(
      {
        id: String(data?.lastConsultationId)
      },
      [String(data?.lastConsultationId)],
      {
        enabled: !!data?.lastConsultationId
      }
    )

  const { data: adverseReactions } =
    usePatientTreatmentServiceGetPatientTreatmentPlanAdverseEffectEntryListEndpoint(
      { id: String(id) },
      [String(id)],
      {
        enabled: !!id
      }
    )

  const textStyle = {
    size: 'xl',
    weight: 600
  } as const

  const isCompleted =
    data?.status === TreatmentPlanStatus.CANCELLED ||
    data?.status === TreatmentPlanStatus.COMPLETED

  const columns: ColumnsType<GetTreatmentPlanPrescribedTreatmentResponse> = [
    {
      key: 'name',
      title: t('consultations.active_substance'),
      render: (_, { internationalName }) => (
        <Text weight={600}>{internationalName?.name}</Text>
      )
    },
    {
      key: 'typeOfAdministration',
      align: 'center',
      title: t('treatments.medication_type'),
      render: (_, { productForm }) => (
        <Text weight={600}>{productForm?.name ?? '-'}</Text>
      )
    },
    {
      key: 'amount',
      align: 'center',
      title: t('common.quantity'),
      render: (_, { amount }) => <Text weight={600}>{amount}</Text>
    },
    {
      key: 'amountToAdminister',
      align: 'center',
      title: t('persons.treatment.treatment_prescribed'),
      render: (_, { amountToAdminister }) => (
        <Text weight={600}> {amountToAdminister ?? '-'}</Text>
      )
    },
    {
      key: 'durationInDays',
      align: 'center',
      title: t('consultations.duration_days'),
      render: (_, { durationInDays }) => (
        <Text weight={600}>{durationInDays ?? '-'}</Text>
      )
    },

    {
      key: 'cron',
      title: t('cron.schedule'),
      render: ({ scheduleExpression }) => (
        <Text weight={600}>
          {scheduleExpression
            ? cronstrue.toString(scheduleExpression, {
                locale: currentLang?.value,
                verbose: true
              })
            : '-'}{' '}
        </Text>
      )
    },
    {
      key: 'startDate',
      title: t('cron.start_data_administration'),
      render: ({ startDate }) => (
        <Text weight={600}>
          {startDate ? dayjs(startDate).format(dayMonthYearBackSlash) : '-'}
        </Text>
      )
    },
    {
      key: 'endDate',
      title: t('cron.end_data_administration'),
      render: ({ endDate }) => (
        <Text weight={600}>
          {endDate ? dayjs(endDate).format(dayMonthYearBackSlash) : '-'}
        </Text>
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('treatments.treatments_details')}
      items={[
        {
          title: t('menu.treatments')
        },
        {
          title: 'Terapia antiretrovirală (TAR)'
        }
      ]}
    >
      <Card loading={isLoading || isLoadingConsultations}>
        <Flex vertical gap={16}>
          <Card title={t('common.person')}>
            <Row gutter={24} align="middle">
              <Col>
                <Flex gap={8} align="center">
                  <Text weight={400}>{t('form.last_name')}:</Text>
                  <Text weight={700}>
                    {person?.firstName} {person?.lastName}
                  </Text>
                  <Tag className={styles.treatmentTypeTag}>
                    {data?.treatmentType?.translation?.[currentLang?.value]}
                  </Tag>
                </Flex>
              </Col>

              <Col>
                <Flex gap={8}>
                  <Text weight={400}>{t('common.phone')}:</Text>
                  <Text weight={700}>
                    {person?.phone ? person?.phone : '-'}
                  </Text>
                </Flex>
              </Col>

              <Col>
                <Flex gap={8}>
                  <Text weight={400}>{t('cases.email_address')}:</Text>
                  <Text weight={700}>
                    {person?.email ? person?.email : '-'}
                  </Text>
                </Flex>
              </Col>
            </Row>
          </Card>

          <Card title={t('treatments.arv_medication')}>
            <Table
              columns={columns}
              scroll={{ x: 'max-content' }}
              dataSource={data?.prescribedTreatments}
              pagination={false}
            />
          </Card>

          <Flex gap={16}>
            <Card
              title={t('treatments.opportunistic_diseases')}
              className="full-width"
            >
              <Flex gap={8} wrap="wrap">
                {latestConsultation?.assessment?.opportunisticDiseases?.length
                  ? latestConsultation?.assessment?.opportunisticDiseases?.map(
                      (diagnostic) => (
                        <Tag key={diagnostic.id} className={styles.tag}>
                          {diagnostic.translation?.[currentLang.value]}
                        </Tag>
                      )
                    )
                  : '-'}
              </Flex>
            </Card>
            <Card
              title={t('treatments.diagnostic_details')}
              className="full-width"
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Flex gap={8}>
                    <Text>{t('treatments.diagnostic_date')}:</Text>
                    {latestConsultation?.generalInformation?.consultationDate
                      ? formatDate(
                          latestConsultation?.generalInformation
                            ?.consultationDate
                        )
                      : '-'}
                  </Flex>
                </Col>

                <Col span={24}>
                  <Flex gap={8} wrap="wrap">
                    {latestConsultation?.assessment?.finalDiagnostics?.length
                      ? latestConsultation?.assessment?.finalDiagnostics?.map(
                          (diagnostic) => (
                            <Tag key={diagnostic.id} className={styles.tag}>
                              {diagnostic.translation?.[currentLang.value]}
                            </Tag>
                          )
                        )
                      : '-'}
                  </Flex>
                </Col>
              </Row>
            </Card>
          </Flex>

          <Card
            className={styles.card}
            title={
              <Flex justify="space-between">
                <Flex gap={48} align="center">
                  <Text {...textStyle}>{t('treatments.tar_treatment')}</Text>

                  <Flex gap={8}>
                    <Text {...textStyle}>{t('treatments.status')}</Text>
                    <TreatmentStatusTag status={data?.status} />
                  </Flex>
                  <Text {...textStyle}>
                    {t('treatments.institution')}: {data?.legalEntity?.name}
                  </Text>
                </Flex>
              </Flex>
            }
            extra={
              !isCompleted &&
              !adverseReactions?.data?.length && (
                <Button
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick()
                  }}
                >
                  {t('treatments.report_reactions')}
                </Button>
              )
            }
          >
            <CollapseDetails data={data} />
          </Card>
        </Flex>
      </Card>

      {isModalOpen && <ReactionsModal id={id} onCancel={onModalCancel} />}
    </PageBreadcrumbs>
  )
}
